import React, { useEffect, useRef, useState } from 'react';
import BrandSymbol from '../ui/BrandSymbol';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { settingsService, type AccountDeletionStatus } from '../../services/settingsService';
import { ResponsiveContainer, ResponsiveContent } from '../ui';

const DeleteAccountAnimation: React.FC = () => {
  const { logoutSession, clearSession } = useSessionPersistence();
  const [deletionStatus, setDeletionStatus] = useState<'deleting' | 'deleted' | 'finalizing' | 'error'>('deleting');
  const cleanupDone = useRef(false);
  const [, setAccountDeletionInformation ] = useState<AccountDeletionStatus | null>(null);
  const [ deletionType, setDeletionType ] = useState<'full' | 'planner' | 'none'>('none');

  const getFrontendUrl = () => {
    const env = import.meta.env.VITE_ENVIRONMENT;
    
    if (deletionType === 'planner') {
      if( env === 'preprod') {
        return 'https://preprod.endofanera.ai/estate-selector';
      }
      else {
        return 'https://my.endofanera.ai/estate-selector';
      }
    }
    else {
      if( env === 'preprod') {
        return 'https://preprod.endofanera.ai';
      }
      else {
        return 'https://my.endofanera.ai';
      }
    }
  };

  const performLogoutCleanup = () => {
    if (cleanupDone.current) return;
    cleanupDone.current = true;

    localStorage.removeItem('account-token');
    localStorage.removeItem('account-refresh-token');
    localStorage.removeItem('refreshToken');
    sessionStorage.clear();

    try {
      clearSession();
      logoutSession();
    } catch (e) {
      console.warn('[DeleteAccountAnimation] Session cleanup warning:', e);
    }
  };

  useEffect(() => {
    let t1: number | undefined;
    let t2: number | undefined;

    const run = async () => {
      try {
        setDeletionStatus('deleting');
        
        const accountStatus = await settingsService.getDeletionStatus();

        if (accountStatus.success) {
          setAccountDeletionInformation(accountStatus);

          if (accountStatus?.canDeleteFullAccount) {
            setDeletionType('full');
            await settingsService.deleteAccount();
            t1 = window.setTimeout(() => {
              setDeletionStatus('finalizing');
              t2 = window.setTimeout(() => setDeletionStatus('deleted'), 2000);
            }, 2500);
          } 
          else if (accountStatus?.canDeletePlannerProfile) {
            setDeletionType('planner');
            await settingsService.deletePlannerProfile();
            t1 = window.setTimeout(() => {
              setDeletionStatus('finalizing');
              t2 = window.setTimeout(() => setDeletionStatus('deleted'), 2000);
            }, 2500);
          }
          else {
            setDeletionType('none');
            console.error('[DeleteAccountAnimation] Failed to delete account:', accountStatus);
            setDeletionStatus('error');
          }
        } else {
          console.error('[DeleteAccountAnimation] Failed to get account deletion information:', accountStatus);
          setDeletionStatus('error');
        }

      } catch (e) {
        console.error('[DeleteAccountAnimation] deletion failed:', e);

        // go straight to finalizing
        setDeletionStatus('finalizing');

        // end in error (shown once, at the end)
        t1 = window.setTimeout(() => {
          setDeletionStatus('error');
        }, 1500);
      }
    };

    run();

    return () => {
      if (t1) window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
    };
  }, []);

  // cleanup regardless of Close click
  useEffect(() => {
    // Pick the moment you consider "logout should happen"
    if ((deletionStatus === 'deleted' || deletionStatus === 'error') && deletionType === 'full') {
      performLogoutCleanup();
    }
  }, [deletionStatus]);

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="flex justify-center pt-8">
          <BrandSymbol size="3xl" showText={true} variant="dark" className="mx-auto mb-8 sm:mb-10 lg:mb-12" />
        </div>

        <div className="flex flex-col items-center justify-center text-center px-6 py-16">
          <h1 className="font-serif text-[#163A5F] text-2xl sm:text-3xl md:text-4xl font-semibold leading-snug max-w-3xl">
            Thank you for taking the time to care for this.
          </h1>

          <p className="mt-10 text-lg sm:text-xl font-medium text-stone-800">
            {deletionStatus === 'deleting' && 'Your account is being deleted...'}
            {deletionStatus === 'finalizing' && 'Logging you out...'}
            {deletionStatus === 'error' && "We couldn’t confirm deletion, but we’re logging you out..."}
            {deletionStatus === 'deleted' && 'The account has been successfully closed.'}
          </p>

          {deletionStatus !== 'deleted' ? (
            <p className="mt-4 text-sm sm:text-base text-stone-500 max-w-2xl">
              A confirmation email will be sent to you.
            </p>
          ) : deletionType === 'full' ? (
            <p className="mt-4 text-sm sm:text-base text-stone-500 max-w-4xl">
              All associated data has been securely deleted in accordance with our privacy and security standards.
              <span className="block font-semibold text-stone-600 mt-1">No further action is needed.</span>
            </p>
          ): deletionType === 'planner' ? (
            <p className="mt-4 text-sm sm:text-base text-stone-500 max-w-4xl">
              The planner profile has been successfully closed.
              <span className="block font-semibold text-stone-600 mt-1">No further action is needed.</span>
            </p>
          ): null}

          {/* ✅ Close now ONLY redirects */}
          {(deletionStatus === 'deleted' || deletionStatus === 'error') && (
            <button
              type="button"
              onClick={() => {
                window.location.href = getFrontendUrl();
              }}
              className="mt-10 rounded-md bg-teal-700 px-6 py-2.5 text-white font-medium hover:bg-teal-800 transition"
            >
              Close
            </button>
          )}
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default DeleteAccountAnimation;
